import React, { useState, forwardRef, useMemo, useEffect, useRef }from 'react';
import io from 'socket.io-client';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import { throttle } from 'throttle-debounce';
import ScoreBoard from '../../components/game/ScoreBoard';
import SwapTeam from '../../components/game/SwapTeam';
import ForfeitTeam from '../../components/game/ForfeitTeam';
import { modal } from '../../components/providers/ModalProvider';

// CRUD
import { fetchAdmin } from '../../hooks/useSwr';
import fetchGql from '../../crud/fetchGql';
import { gameGql } from '../../crud/gql/Games';

// Service
import { socket } from '../../components/service/socket';

import styles from './game.module.css';

import {
    Grid,
    Box,
    H1,
    Btn,
    Type,
} from '@saladbob/sassafras';

dayjs.extend(utc);
dayjs.extend(timezone);

const statusLabels = {
    pending: 'Pending',
    forfeit: 'Forfeit',
    live: 'Live',
    ended: 'Final',
    waiting: 'Waiting',
    bye: 'Bye',
}

const CustomInput = forwardRef(({ value, onClick, color }: {value?: string, onClick?: () => void, color?: string }, ref) => (
    <Type
        display="block"
        font="scoreBoardSm"
        color="white"
        onClick={onClick}
        ref={ref}
        padding="sm"
        rounded="sm"
        bgColor={color}
        width="min-content"
        nowrap
        margin={[0, 'auto']}
    >
        {value}
    </Type>
));

const TimeStamp = ({ onChange, time, color, status, admin }) => {
    const [startDate, setStartDate] = useState(new Date(time));

    const handleChange = (date) => {
        setStartDate(date);
        if (typeof onChange === 'function') {
            onChange(date);
        }
    };

    if (!admin) {
        return (
            <CustomInput
                color={color}
                value={
                    status === 'ended' || status === 'bye' || status === 'forfeit'
                        ? statusLabels[status]
                        : dayjs(time).tz('America/New_York').format('h:mm A')
                }
            />
        );
    }
    return (
        <DatePicker
            calendarClassName={styles.datePicker}
            selected={startDate}
            dateFormat="h:mma"
            onChange={handleChange}
            showTimeSelect
            customInput={<CustomInput color={color} />}
        />
    )
};

export async function getStaticPaths() {
    return {
        paths: [],
        fallback: 'blocking', // can also be true or 'blocking'
    }
}

export async function getStaticProps({ params }) {
    const game = await fetchGql({ query: gameGql, variables: { id: params.id }, secret_key: process.env.RATE_LIMIT_SECRET_KEY});

    if (game.errors) {
        return {
            notFound: true,
        }
    }

    return {
        props: {
            metaTitle: game.data.Game.title,
            game: game.data.Game,
        },
    }
}

export default function Game({ game }) {
    const { admin, isLoading } = fetchAdmin();
    const [score1, setScore1] = useState(game.team1Score || 0);
    const [score2, setScore2] = useState(game.team2Score || 0);
    const [status, setStatus] = useState(game.status || 'pending');
    const [forfeit, setForfeit] = useState(game.forfeit);
    const [winner, setWinner] = useState(game.winner);
    const [team1, setTeam1] = useState(game.team1);
    const [team2, setTeam2] = useState(game.team2);
    const adminRef = useRef(admin);
    const teamKey = useRef();

    const teamColor1 = useMemo(() => team1?.color1 || 'primary700', [team1]);
    const teamColor2 = useMemo(() => team2?.color2 || 'secondary700', [team2]);

    const patchTime = (time) => {
        fetch(`${process.env.NEXT_PUBLIC_API_URL}/game/${game.id}?depth=0`, {
            method: 'PATCH',
            credentials: 'include',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                time,
            }),
        });
    }

    const patchStatus = useMemo(() => throttle(150, (status) => {           
        fetch(`${process.env.NEXT_PUBLIC_API_URL}/game/${game.id}?depth=0`, {
            method: 'PATCH',
            credentials: 'include',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                status: status,
            }),
        });
        setStatus(status);
    }), []);


    const patchScore1 = useMemo(() => throttle(250, async (score) => {
        await fetch(`${process.env.NEXT_PUBLIC_API_URL}/game/${game.id}?depth=0`, {
            method: 'PATCH',
            credentials: 'include',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                team1Score: score,
            }),
        });
    }), []);

    const patchScore2 = useMemo(() => throttle(250, async (score) => {
        await fetch(`${process.env.NEXT_PUBLIC_API_URL}/game/${game.id}?depth=0`, {
            method: 'PATCH',
            credentials: 'include',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                team2Score: score,
            }),
        });
    }), []);

    const onSwapTeam = (team) => {
        modal.close();
        if (teamKey.current === 'team1') {
            if (team2 && !team2) {
                setStatus('waiting');
            }
            setTeam1(team);
        } else {
            if (team1 && !team2) {
                setStatus('waiting');
            }
            setTeam2(team);
        }
    }

    const changeTeam = (tk) => {
        teamKey.current = tk;
        modal.open({
            content: (
                <SwapTeam
                    teamKey={tk}
                    team1={team1}
                    team2={team2}
                    game={game}
                    court={game.court}
                    onSwap={onSwapTeam}
                />
            ),
        });
    }

    const updateStatus = (data) => {
        if (!adminRef.current && data.id === game.id) {
            setStatus(data.status);
        }
    };

    const updateScores = (data) => {
        if (!adminRef.current && data.id === game.id) {
            setScore1(data.team1Score);
            setScore2(data.team2Score);
        }
    };

    const updateForfeit = (data) => {
        if (!adminRef.current && data.id === game.id) {
            setForfeit(data.forfeit);
        }
    };

    const updateWinner = (data) => {
        if (!adminRef.current && data.id === game.id) {
            setWinner(data.winner);
        }
    }

    const updateTeam1 = (data) => {
        if (!adminRef.current && data.id === game.id) {
            setTeam1(data.team1);
        }
    }

    const updateTeam2 = (data) => {
        if (!adminRef.current && data.id === game.id) {
            setTeam2(data.team2);
        }
    }

    useEffect(() => {
        socket.on('scoreChange', updateScores);
        socket.on('gameStatusChange', updateStatus);
        socket.on('gameForfeitChange', updateForfeit);
        socket.on('gameTeam1Change', updateTeam1);
        socket.on('gameTeam2Change', updateTeam2);
        socket.on('gameWinnerChange', updateWinner);
        return () => {
            socket.off('scoreChange', updateScores);
            socket.off('gameStatusChange', updateStatus);
            socket.off('gameForfeitChange', updateForfeit);
            socket.off('gameTeam1Change', updateTeam1);
            socket.off('gameTeam2Change', updateTeam2);
            socket.off('gameWinnerChange', updateWinner);
        }
    }, []);

    useEffect(() => {
        adminRef.current = admin;
    }, [admin]);

    return (<>
        <Grid
            columns={{ default: '1fr', laptop: '1fr 1fr' }}
            minHeight={{ phone: admin ? 'calc(100vh - 106px)' : '100vh' , laptop: '100vh' }}
        >
            <ScoreBoard
                status={status}
                team={team1}
                teamKey={'team1'}
                place={game.place1}
                winner={winner}
                score={score1}
                setScore={(value) => {
                    setScore1(value);
                    patchScore1(value);
                }}
                updateScore={updateScore1}
                startGame={startGame}
                forfeit={forfeit}
                patchForfeit={patchForfeit}
                patchStatus={patchStatus}
                changeTeam={changeTeam}
                bye={team1 && !team2}
                admin={admin}
                color={teamColor1}
                pb={{ phone: 'xl', laptop: 'md' }}
                pt="md"
            />
            <ScoreBoard
                status={status}
                team={team2}
                place={game.place2}
                teamKey={'team2'}
                winner={winner}
                score={score2}
                setScore={(value) => {
                    setScore2(value);
                    patchScore2(value);
                }}
                updateScore={updateScore2}
                endGame={endGame}
                forfeit={forfeit}
                admin={admin}
                patchForfeit={patchForfeit}
                patchStatus={patchStatus}
                changeTeam={changeTeam}
                bye={team2 && !team1}
                color={teamColor2}
                pt={{ phone: 'xl', laptop: 'md' }}
                pb="md"
            />
            <div
                className={styles.center}
            >
                <Box
                    className={styles.vs}
                    width="100px"
                    height="100px"
                    rounded="100%"
                    margin="auto"
                    verticalAlign="center"
                    align="center"
                    alignItems="center"
                    bgColor={teamColor1}
                >
                    <H1 align="center" color="white" lineHeight="1em">VS.</H1>
                </Box>
                <div className={styles.gameTime}>
                    <TimeStamp onChange={patchTime} time={game.time} color={teamColor2} status={status} admin={admin} />
                </div>
            </div>
        </Grid>
        {admin && (
            <div className={styles.mobileAdmin}>
                <Grid columns="1fr 1fr" bgColor="black" padding="sm">
                    <Box padding={['xs', '50px', 'xs', 'xs']} justify="center">
                        {(status === 'waiting' || status === 'ended') && (
                            <Btn size="lg" rounded="md" color="white" font="p" outline onClick={startGame}>
                                <Type weight="bold">START</Type>
                            </Btn>
                        )}
                    </Box>
                    {(status === 'live') && (
                        <Box padding={['xs', 'xs', 'xs', '50px']} justify="center">
                            <Btn size="lg" rounded="md" color="white" font="p" outline onClick={endGame}>
                                <Type weight="bold">END</Type>
                            </Btn>
                        </Box>
                    )}
                </Grid>
            </div>
        )}
    </>);

    function endGame() {
        if (score1 > score2) {
            setWinner(team1);
        } else {
            setWinner(team2);
        }
        setStatus('ended');
        patchStatus('ended');
    };

    function startGame() {
        setStatus('live');
        patchStatus('live');
    };

    function updateScore1(score) {
        const newScore = Math.max(score1 + score, 0);
        setScore1(newScore);
        patchScore1(newScore);
    };

    function updateScore2(score) {
        const newScore = Math.max(score2 + score, 0);
        setScore2(newScore);
        patchScore2(newScore);
    };

    async function patchForfeit(id) {
        if (!id) {
            setForfeit(null);
            setStatus('waiting');
            await fetch(`${process.env.NEXT_PUBLIC_API_URL}/game/${game.id}?depth=0`, {
                method: 'PATCH',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    forfeit: null,
                    status: 'waiting'
                }),
            });
            return;
        } else {
            forfeitTeam();
        }
    };

    function onForfeit(id) {
        setForfeit(id === team1.id ? team1 : team2);
        if (id === team1.id) {
            setWinner(team2);
        } else {
            setWinner(team1);
        }
        onSwap();
    }

    function onSwap () {
        modal.close();
        teamKey.current = null;
    }

    function forfeitTeam() {
        modal.open({
            content: (
                <ForfeitTeam
                    team1={team1}
                    team2={team2} 
                    game={game}
                    onForfeit={onForfeit}
                />
            ),
        });
    }
};
